import type {Capability} from './types';

/**
 * Static capability registry.
 * Capabilities define what nodes can do in our platform.
 */
const CAPABILITIES = {
  // ============================================
  // Triggers - Workflow entry points
  // ============================================
  manual_trigger: {
    type: 'trigger',
    icon: 'hand-pointer',
    name: 'Manual Trigger',
    label: 'When',
    description: 'Manually start the workflow',
    defaultInputs: [],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
  schedule_trigger: {
    type: 'trigger',
    icon: 'calendar',
    name: 'Schedule Trigger',
    label: 'When',
    description: 'Start the workflow on a schedule',
    defaultInputs: [],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
  webhook_trigger: {
    type: 'trigger',
    icon: 'webhook',
    name: 'Webhook Trigger',
    label: 'When',
    description: 'Start the workflow via HTTP webhook',
    defaultInputs: [],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
  event_trigger: {
    type: 'trigger',
    icon: 'zap',
    name: 'Event Trigger',
    label: 'When',
    description: 'Start the workflow on a platform event',
    defaultInputs: [],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },

  // ============================================
  // AI - AI/ML nodes
  // ============================================
  ai_agent: {
    type: 'ai',
    icon: 'sparkles',
    name: 'AI Agent',
    label: 'Then',
    description: 'AI agent interaction',
    defaultInputs: [
      {id: 'input_main', name: 'main', type: 'any'},
      {id: 'input_ai_languageModel', name: 'ai_languageModel', type: 'object'},
      {id: 'input_ai_outputParser', name: 'ai_outputParser', type: 'object'},
    ],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
  ai_model: {
    type: 'ai',
    icon: 'brain',
    name: 'AI Model',
    label: 'Model',
    description: 'Language model configuration',
    defaultInputs: [],
    defaultOutputs: [
      {id: 'output_ai_languageModel', name: 'ai_languageModel', type: 'object'},
    ],
  },
  ai_output_parser: {
    type: 'ai',
    icon: 'list-tree',
    name: 'Output Parser',
    label: 'Parse',
    description: 'Parses AI model output into structured data',
    defaultInputs: [],
    defaultOutputs: [
      {id: 'output_ai_outputParser', name: 'ai_outputParser', type: 'object'},
    ],
  },

  // ============================================
  // Actions - Perform operations
  // ============================================
  http_request: {
    type: 'action',
    icon: 'globe',
    name: 'HTTP Request',
    label: 'Fetch',
    description: 'Make HTTP requests',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
  webflow_action: {
    type: 'action',
    icon: 'webflow',
    name: 'Webflow',
    label: 'Webflow',
    description: 'Interact with Webflow',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
  slack_action: {
    type: 'action',
    icon: 'slack',
    name: 'Slack',
    label: 'Slack',
    description: 'Send Slack messages',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
  mcp_tool: {
    type: 'action',
    icon: 'tool',
    name: 'MCP Tool',
    label: 'Tool',
    description: 'Call MCP tools',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
  data_storage: {
    type: 'action',
    icon: 'database',
    name: 'Data Storage',
    label: 'Storage',
    description: 'Store/retrieve data',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },

  // ============================================
  // Transformers - Transform data
  // ============================================
  code: {
    type: 'transformer',
    icon: 'code',
    name: 'Code',
    label: 'Code',
    description: 'Custom code execution',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
  set_values: {
    type: 'transformer',
    icon: 'dynamic-content',
    name: 'Set Values',
    label: 'Define',
    description: 'Set data values',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
  filter: {
    type: 'transformer',
    icon: 'filter',
    name: 'Filter',
    label: 'Filter',
    description: 'Filter data',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
  aggregate: {
    type: 'transformer',
    icon: 'layers',
    name: 'Aggregate',
    label: 'Aggregate',
    description: 'Aggregate data',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
  data_transform: {
    type: 'transformer',
    icon: 'shuffle',
    name: 'Data Transform',
    label: 'Transform',
    description: 'Transform data',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
  merge: {
    type: 'transformer',
    icon: 'merge',
    name: 'Merge',
    label: 'Merge',
    description: 'Merge multiple data streams',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },

  // ============================================
  // Control Flow - Flow control
  // ============================================
  conditional: {
    type: 'control_flow',
    icon: 'git-branch',
    name: 'Conditional',
    label: 'If',
    description: 'Conditional branching',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [
      {id: 'output_true', name: 'true', type: 'any'},
      {id: 'output_false', name: 'false', type: 'any'},
    ],
  },
  wait: {
    type: 'control_flow',
    icon: 'timer',
    name: 'Wait',
    label: 'Wait',
    description: 'Wait/pause execution',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
  loop: {
    type: 'control_flow',
    icon: 'repeat',
    name: 'Loop',
    label: 'Loop',
    description: 'Loop over items in batches',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [
      {id: 'output_done', name: 'done', type: 'any'},
      {id: 'output_loop', name: 'loop', type: 'any'},
    ],
  },
  noop: {
    type: 'action',
    icon: 'circle-dot',
    name: 'No Operation',
    label: 'Pass',
    description: 'Placeholder node that passes data through',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
  respond_to_webhook: {
    type: 'action',
    icon: 'webhook',
    name: 'Respond to Webhook',
    label: 'Respond',
    description: 'Send the HTTP response for a webhook trigger',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },

  // ============================================
  // Unknown - Fallback for unmapped nodes
  // ============================================
  unknown: {
    type: 'action',
    icon: 'help-circle',
    name: 'Unknown',
    label: 'Unknown',
    description: 'Unknown node type',
    defaultInputs: [{id: 'input_main', name: 'main', type: 'any'}],
    defaultOutputs: [{id: 'output_main', name: 'main', type: 'any'}],
  },
} as const satisfies Record<string, Capability>;

const capabilityMap: ReadonlyMap<string, Capability> = new Map(
  Object.entries(CAPABILITIES)
);

export const getCapability = (key: string): Capability | undefined =>
  capabilityMap.get(key);

export const hasCapability = (key: string): boolean => capabilityMap.has(key);

export const getCapabilityKeys = (): ReadonlyArray<string> =>
  Object.keys(CAPABILITIES);

export type CapabilityKey = keyof typeof CAPABILITIES;

export const CAPABILITY_KEYS = Object.keys(CAPABILITIES) as CapabilityKey[];
